import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import CTAButtons from './CTAButtons';
import { countries, getCountryByCode } from '../data/countries';
import { getGeoLocation, submitLead, getDialCodeFromCountry, navigateToApplication } from '../services/api';

const Hero = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [geoData, setGeoData] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+1',
    phone: '',
    message: ''
  });

  // Fetch geolocation on component mount
  useEffect(() => {
    const fetchGeoLocation = async () => {
      const result = await getGeoLocation();
      if (result.success && result.data) {
        setGeoData(result.data);
        
        // Set default country code based on geo location
        if (result.data.country) {
          const dialCode = getDialCodeFromCountry(result.data.country);
          setFormData(prev => ({ ...prev, countryCode: `+${dialCode}` }));
        }
      }
    };
    
    fetchGeoLocation();
  }, []);

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Remove non-digits
    const currentCountry = getCountryByCode(formData.countryCode);
    
    // Limit to max length for selected country
    if (value.length <= currentCountry.maxLength) {
      setFormData({ ...formData, phone: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone || !formData.message) {
      toast({
        title: 'Error',
        description: 'Please fill in all required fields',
        variant: 'destructive'
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Get country code from dial code
      const dialCode = parseInt(formData.countryCode.replace('+', ''));
      const country = geoData?.country || 'US';
      const ipAddress = geoData?.ipAddress || '0.0.0.0';

      // Submit lead using centralized API service
      const result = await submitLead({
        name: formData.name,
        email: formData.email,
        countryDialCode: dialCode,
        country: country,
        phoneNumber: formData.phone, // Only digits, no prefix
        ipAddress: ipAddress,
        description: formData.message,
        pageUrl: window.location.href
      });

      if (result.success) {
        toast({
          title: 'Success!',
          description: result.message || 'Your consultation request has been submitted.',
        });

        // Reset form
        setFormData({
          name: '',
          email: '',
          countryCode: formData.countryCode,
          phone: '',
          message: ''
        });

        // Redirect to application page
        setTimeout(() => {
          navigateToApplication(result.customerId);
        }, 1500);

      } else {
        toast({
          title: 'Error',
          description: result.message,
          variant: 'destructive'
        });
      }

    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: 'Error',
        description: 'Something went wrong. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative bg-gradient-to-br from-emerald-50 via-white to-blue-50 py-16 overflow-hidden">
      {/* Abstract Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Large circles */}
        <div className="absolute -top-20 -right-20 w-96 h-96 bg-gradient-to-br from-emerald-200/30 to-emerald-100/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] bg-gradient-to-tr from-blue-200/30 to-blue-100/20 rounded-full blur-3xl"></div>
        
        {/* Medium circles */}
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-gradient-to-br from-purple-200/20 to-transparent rounded-full blur-2xl"></div>
        <div className="absolute bottom-1/4 right-1/3 w-80 h-80 bg-gradient-to-bl from-emerald-300/20 to-transparent rounded-full blur-2xl"></div>
        
        {/* Small accent circles */}
        <div className="absolute top-10 left-1/3 w-32 h-32 bg-emerald-300/20 rounded-full blur-xl"></div>
        <div className="absolute bottom-20 right-1/4 w-40 h-40 bg-blue-300/20 rounded-full blur-xl"></div>
        
        {/* Geometric Lines and Shapes - Using absolute pixel values */}
        <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg" style={{ pointerEvents: 'none' }}>
          {/* Diagonal lines */}
          <line x1="0" y1="100" x2="300" y2="0" stroke="rgba(16, 185, 129, 0.1)" strokeWidth="2" />
          <line x1="80%" y1="0" x2="100%" y2="200" stroke="rgba(59, 130, 246, 0.1)" strokeWidth="2" />
          <line x1="0" y1="60%" x2="200" y2="100%" stroke="rgba(168, 85, 247, 0.08)" strokeWidth="1.5" />
          
          {/* Curved lines */}
          <path d="M 0,150 Q 200,100 400,200" stroke="rgba(16, 185, 129, 0.12)" strokeWidth="2" fill="none" />
          <path d="M 1800,300 Q 1600,200 1800,100" stroke="rgba(59, 130, 246, 0.1)" strokeWidth="1.5" fill="none" />
          
          {/* Small circles/dots pattern */}
          <circle cx="15%" cy="20%" r="3" fill="rgba(16, 185, 129, 0.2)" />
          <circle cx="25%" cy="15%" r="2" fill="rgba(59, 130, 246, 0.2)" />
          <circle cx="85%" cy="25%" r="4" fill="rgba(16, 185, 129, 0.15)" />
          <circle cx="90%" cy="35%" r="2.5" fill="rgba(168, 85, 247, 0.2)" />
          <circle cx="12%" cy="75%" r="3.5" fill="rgba(59, 130, 246, 0.18)" />
          <circle cx="88%" cy="80%" r="3" fill="rgba(16, 185, 129, 0.15)" />
          
          {/* Triangular shapes - Fixed coordinates */}
          <polygon points="10,10 30,50 50,20" fill="rgba(16, 185, 129, 0.08)" />
          <polygon points="1700,100 1750,150 1680,180" fill="rgba(59, 130, 246, 0.1)" />
        </svg>
        
        {/* Floating geometric shapes */}
        <div className="absolute top-20 right-1/4 w-16 h-16 border-2 border-emerald-300/30 rotate-45"></div>
        <div className="absolute bottom-32 left-1/3 w-12 h-12 border-2 border-blue-300/25 rounded-lg rotate-12"></div>
        <div className="absolute top-1/2 right-1/4 w-20 h-20 border border-purple-300/20 rounded-full"></div>
      </div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-12 items-center max-w-7xl mx-auto">
          {/* Content Section - Left Side (60%) */}
          <div className="text-left lg:pr-8">
            <div className="inline-block mb-6 px-4 py-2 bg-emerald-100 text-emerald-800 rounded-full text-xl font-semibold">
              Secure Your Brand
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-8 leading-tight">
              Simple and Fast Trademark Registration for Only{' '}
              <span className="text-emerald-600 text-6xl">$45</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-600 mb-10 leading-relaxed">
              Protect your brand identity with Global Trademark Pro. Expert trademark registration services at your fingertips.
            </p>
            
            <CTAButtons className="" />
          </div>

          {/* Form Section - Right Side (40%) */}
          <div className="bg-white rounded-2xl shadow-2xl p-7 border border-gray-100 max-w-md">
            <h3 className="text-xl font-bold text-gray-900 mb-2 text-center">Get Your Free Trademark Consultation</h3>
            <p className="text-emerald-600 text-center mb-5 font-semibold text-xl">Start Protecting Your Brand Today!</p>
            
            <form onSubmit={handleSubmit} className="space-y-3.5">
              <div>
                <Input
                  type="text"
                  placeholder="Full Name *"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="h-11"
                  required
                />
              </div>
              <div>
                <Input
                  type="email"
                  placeholder="Email Address *"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="h-11"
                  required
                />
              </div>
              <div className="flex gap-2">
                <Select value={formData.countryCode} onValueChange={(value) => setFormData({ ...formData, countryCode: value, phone: '' })}>
                  <SelectTrigger className="w-28 h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {countries.map((country, index) => (
                      <SelectItem key={index} value={country.code}>
                        {country.flag} {country.code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  type="tel"
                  placeholder="Phone Number *"
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  className="flex-1 h-11"
                  required
                />
              </div>
              <div>
                <Textarea
                  placeholder="Message *"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="min-h-[90px] resize-none"
                  required
                />
              </div>
              <Button type="submit" disabled={isSubmitting} className="w-full h-11 bg-gray-900 hover:bg-gray-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed">
                {isSubmitting ? 'Submitting...' : 'Get Free Consultation'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Hero;